import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { auth } from '../../firebase/config';
import { useAuth } from '../../hooks/useAuth';
import { UserRole } from '../../types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  requireAuth?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requireAuth = true,
}) => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();
  const firebaseUser = auth.currentUser;
  const isActuallyAuthenticated = isAuthenticated || !!firebaseUser;

  // Show loading while checking authentication, or while user document is loading when firebase user is authenticated
  if (isLoading || (requireAuth && !!firebaseUser && !user)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Redirect to login if authentication is required but user is not authenticated
  if (requireAuth && !isActuallyAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to my-tasks if user is authenticated but accessing auth pages
  if (!requireAuth && isActuallyAuthenticated) {
    return <Navigate to="/my-tasks" replace />;
  }

  // Check role-based access
  if (requiredRole && user) {
    const roleHierarchy: Record<UserRole, number> = {
      user: 1,
      task_creator: 2,
      template_creator: 3,
      api_manager: 4,
      admin: 5,
      account_owner: 6,
    };

    const userRoleLevel = roleHierarchy[user.role];
    const requiredRoleLevel = roleHierarchy[requiredRole];

    if (userRoleLevel < requiredRoleLevel) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-secondary-900 mb-4">Access Denied</h2>
            <p className="text-secondary-600 mb-4">
              You don't have permission to access this page.
            </p>
            <button
              onClick={() => window.history.back()}
              className="text-primary-600 hover:text-primary-500 font-medium"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;